import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';

const DashboardOverview = () => {
  // Mock dashboard data
  const kpiData = [
    {
      title: 'Ingresos Totales',
      value: '€1,245,890',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'Euro',
      description: 'Ingresos del período actual'
    },
    {
      title: 'Nuevos Clientes',
      value: '156',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'UserPlus',
      description: 'Clientes adquiridos este mes'
    },
    {
      title: 'Productos Vendidos',
      value: '2,847',
      change: '-3.1%',
      changeType: 'negative',
      icon: 'Package',
      description: 'Unidades vendidas'
    },
    {
      title: 'Tasa de Conversión',
      value: '24.8%',
      change: '+1.9%',
      changeType: 'positive',
      icon: 'TrendingUp',
      description: 'De presupuestos a ventas'
    }
  ];

  const salesTrendData = [
    { month: 'Ene', ventas: 85000, presupuestos: 95000 },
    { month: 'Feb', ventas: 92000, presupuestos: 110000 },
    { month: 'Mar', ventas: 78000, presupuestos: 88000 },
    { month: 'Abr', ventas: 105000, presupuestos: 125000 },
    { month: 'May', ventas: 118000, presupuestos: 140000 },
    { month: 'Jun', ventas: 124000, presupuestos: 145000 },
    { month: 'Jul', ventas: 135000, presupuestos: 155000 }
  ];

  const categoryData = [
    { name: 'Electrónica', value: 35, amount: 450000 },
    { name: 'Herramientas', value: 28, amount: 380000 },
    { name: 'Materiales', value: 22, amount: 290000 },
    { name: 'Equipos', value: 10, amount: 130000 },
    { name: 'Otros', value: 5, amount: 68000 }
  ];

  const inventoryData = [
    { categoria: 'Electrónica', stock: 450, ventas: 890 },
    { categoria: 'Herramientas', stock: 320, ventas: 654 },
    { categoria: 'Materiales', stock: 180, ventas: 432 },
    { categoria: 'Equipos', stock: 95, ventas: 234 },
    { categoria: 'Otros', stock: 65, ventas: 156 }
  ];

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const getChangeColor = (changeType) => {
    return changeType === 'positive' ? 'text-success' : 'text-error';
  };

  const getChangeIcon = (changeType) => {
    return changeType === 'positive' ? 'TrendingUp' : 'TrendingDown';
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {kpiData.map((kpi, index) => (
          <div key={index} className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${
                  index === 0 ? 'bg-success-100' :
                  index === 1 ? 'bg-primary-100' :
                  index === 2 ? 'bg-warning-100' : 'bg-info-100'
                }`}>
                  <Icon 
                    name={kpi.icon} 
                    size={20} 
                    className={
                      index === 0 ? 'text-success' :
                      index === 1 ? 'text-primary' :
                      index === 2 ? 'text-warning' : 'text-info'
                    } 
                  />
                </div>
                <div>
                  <h3 className="text-sm font-body-medium text-text-secondary">
                    {kpi.title}
                  </h3>
                  <p className="text-xs text-text-muted">
                    {kpi.description}
                  </p>
                </div>
              </div>
              <div className={`flex items-center space-x-1 ${getChangeColor(kpi.changeType)}`}>
                <Icon name={getChangeIcon(kpi.changeType)} size={14} />
                <span className="text-xs font-body-semibold">
                  {kpi.change}
                </span>
              </div>
            </div>
            <div className="text-2xl font-heading-semibold text-text-primary">
              {kpi.value}
            </div>
          </div>
        ))}
      </div>

      {/* Sales Trend Chart */}
      <div className="card">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-heading-semibold text-text-primary">
                Tendencia de Ventas
              </h3>
              <p className="text-sm text-text-secondary">
                Comparación de ventas vs presupuestos generados
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-md transition-micro">
                <Icon name="Download" size={16} />
              </button>
              <button className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-md transition-micro">
                <Icon name="Maximize" size={16} />
              </button>
            </div>
          </div>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={salesTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [`€${value.toLocaleString()}`, '']} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="presupuestos" 
                stackId="1" 
                stroke={colors[1]} 
                fill={colors[1]} 
                fillOpacity={0.3}
                name="Presupuestos"
              />
              <Area 
                type="monotone" 
                dataKey="ventas" 
                stackId="1" 
                stroke={colors[0]} 
                fill={colors[0]} 
                fillOpacity={0.6}
                name="Ventas"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Distribution and Inventory Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="card">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-heading-semibold text-text-primary">
              Distribución por Categoría
            </h3>
            <p className="text-sm text-text-secondary">
              Ventas por categoría de producto
            </p>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  dataKey="value"
                  nameKey="name"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`, '']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Inventory Status */}
        <div className="card">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-heading-semibold text-text-primary">
              Estado del Inventario
            </h3>
            <p className="text-sm text-text-secondary">
              Stock vs ventas por categoría
            </p>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={inventoryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="categoria" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="stock" fill={colors[0]} name="Stock" />
                <Bar dataKey="ventas" fill={colors[1]} name="Ventas" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="card">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Insights Rápidos
          </h3>
          <p className="text-sm text-text-secondary">
            Hallazgos clave y recomendaciones
          </p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-success-50 border border-success-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-success" />
                <span className="text-sm font-body-semibold text-success">Crecimiento</span>
              </div>
              <p className="text-sm text-text-primary">
                Las ventas han aumentado un 12.5% comparado con el período anterior
              </p>
            </div>
            <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertTriangle" size={16} className="text-warning" />
                <span className="text-sm font-body-semibold text-warning">Atención</span>
              </div>
              <p className="text-sm text-text-primary">
                7 productos tienen stock por debajo del mínimo recomendado
              </p>
            </div>
            <div className="p-4 bg-info-50 border border-info-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Lightbulb" size={16} className="text-info" />
                <span className="text-sm font-body-semibold text-info">Oportunidad</span>
              </div>
              <p className="text-sm text-text-primary">
                La categoría "Electrónica" muestra potencial de crecimiento del 20%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;